import { formatOnayLabel, formatReportDate, recordingFilenameFromUrl } from "@/utils/tlRecordingReportHelpers";
import { formatWeekRangeLabel, getCurrentIsoWeekContext, getIsoWeekAndYear, resolveIsoYearForRow } from "@/utils/isoWeek";

export { formatOnayLabel, formatReportDate, recordingFilenameFromUrl, formatWeekRangeLabel, resolveIsoYearForRow };

const WEEK_DATE_COLUMNS = [
  { key: "week_number", label: "Hafta", type: "week_display" },
  { key: "report_date", label: "Tarih", type: "date" },
];

export const getLocalTodayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const defaultWeekDateFieldsForNewRow = () => {
  const today = getLocalTodayIso();
  const { week } = getIsoWeekAndYear(new Date(today.replace(/-/g, "/")));
  return { week_number: week, report_date: today };
};

export const MANAGER_REPORT_TYPES = [
  "tl-yeni-asistan",
  "kalite-degerlendirme",
  "yeni-asistan-kalite",
];

/** Kalite personeli listesi filtresi (Tüm kalite personeli) olan rapor tipleri */
export const MANAGER_REPORT_TYPES_WITH_QUALITY_FILTER = [
  "kalite-degerlendirme",
  "yeni-asistan-kalite",
];

export function managerReportHasQualityPersonnelFilter(reportType) {
  return MANAGER_REPORT_TYPES_WITH_QUALITY_FILTER.includes(reportType);
}

const BASE_KALITE_COLUMNS = [
  { key: "quality_user_id", label: "Kalite Personeli", type: "quality_select" },
  { key: "leader_user_id", label: "Takım Lideri", type: "leader_select" },
  { key: "agent_id", label: "Asistan İsmi", type: "agent_select" },
  { key: "work_duration", label: "Personel Çalışma Süresi", type: "readonly" },
];

const KALITE_TAIL_COLUMNS = [
  { key: "call1_recording_url", label: "1. Görüşme Ses Kaydı", type: "recording_url" },
  { key: "ordeleme", label: "İrdeleme", type: "yesno" },
  { key: "mr_isteme", label: "MR İsteme", type: "yesno" },
  { key: "call1_evaluation", label: "1. Görüşme Değerlendirme", type: "text" },
  { key: "call2_recording_url", label: "2. Görüşme Ses Kaydı", type: "recording_url" },
  { key: "mr_tepinme", label: "MR Üstünde Tepinme", type: "yesno" },
  { key: "tedirginlik", label: "Tedirginlik", type: "yesno" },
  { key: "uzmanlik", label: "Uzmanlık", type: "yesno" },
  { key: "call2_evaluation", label: "2. Görüşme Değerlendirme", type: "text" },
];

const REPORT_CONFIGS = {
  "tl-yeni-asistan": {
    title: "Takım lideri Yeni Asistan Raporu",
    subtitle: "Takım lideri yeni asistan günlük takip ve karar formu",
    columns: [
      { key: "leader_user_id", label: "Takım Lideri", type: "leader_select" },
      { key: "agent_id", label: "Asistan İsmi", type: "agent_select" },
      ...WEEK_DATE_COLUMNS,
      { key: "work_duration", label: "Personel Çalışma Süresi", type: "readonly" },
      { key: "deficiencies_action", label: "2. Gün — Eksiklikler / Aksiyon planı", type: "text" },
      { key: "development_report", label: "4. Gün — Genel Rapor — Gelişim gösterdi mi?", type: "text" },
      { key: "decision_text", label: "5. Gün — KARAR", type: "text" },
    ],
  },
  "kalite-degerlendirme": {
    title: "Kalite Değerlendirme Raporu",
    subtitle: "Kalite ekibi değerlendirme formu",
    columns: [
      ...BASE_KALITE_COLUMNS,
      ...WEEK_DATE_COLUMNS,
      { key: "yeni_dataya_gecis_tarihi", label: "Yeni Dataya Geçtiği Tarih", type: "readonly" },
      ...KALITE_TAIL_COLUMNS,
    ],
  },
  "yeni-asistan-kalite": {
    title: "Yeni Asistan Kalite Değerlendirme Raporu",
    subtitle: "Yeni asistan kalite değerlendirme ve normal data kararı",
    columns: [
      ...BASE_KALITE_COLUMNS,
      ...WEEK_DATE_COLUMNS,
      ...KALITE_TAIL_COLUMNS,
      { key: "normal_dataya_gecebilir", label: "Normal Dataya Geçebilir mi", type: "yesno" },
      { key: "reason_text", label: "Sebebi nedir?", type: "text" },
    ],
  },
};

export const MANAGER_REPORT_VIEW_TO_TYPE = {
  "mgr-tl-yeni-asistan": "tl-yeni-asistan",
  "mgr-kalite-degerlendirme": "kalite-degerlendirme",
  "mgr-yeni-asistan-kalite": "yeni-asistan-kalite",
};

export const MANAGER_REPORT_SPREADSHEET_VIEW_IDS = Object.keys(MANAGER_REPORT_VIEW_TO_TYPE);

export const getReportConfig = (reportType) => REPORT_CONFIGS[reportType] || null;

export const getReportColumns = (reportType) => getReportConfig(reportType)?.columns || [];

const fieldKeysForType = (reportType) => getReportColumns(reportType).map((c) => c.key);

export const EMPTY_ROW = {
  "tl-yeni-asistan": {
    leader_user_id: "",
    leader_name: "",
    agent_id: "",
    assistant_name: "",
    week_number: "",
    report_date: "",
    work_duration: "",
    deficiencies_action: "",
    development_report: "",
    decision_text: "",
  },
  "kalite-degerlendirme": {
    quality_user_id: "",
    quality_user_name: "",
    leader_user_id: "",
    leader_name: "",
    agent_id: "",
    assistant_name: "",
    week_number: "",
    report_date: "",
    work_duration: "",
    yeni_dataya_gecis_tarihi: "",
    call1_recording_url: "",
    ordeleme: "",
    mr_isteme: "",
    call1_evaluation: "",
    call2_recording_url: "",
    mr_tepinme: "",
    tedirginlik: "",
    uzmanlik: "",
    call2_evaluation: "",
  },
  "yeni-asistan-kalite": {
    quality_user_id: "",
    quality_user_name: "",
    leader_user_id: "",
    leader_name: "",
    agent_id: "",
    assistant_name: "",
    week_number: "",
    report_date: "",
    work_duration: "",
    call1_recording_url: "",
    ordeleme: "",
    mr_isteme: "",
    call1_evaluation: "",
    call2_recording_url: "",
    mr_tepinme: "",
    tedirginlik: "",
    uzmanlik: "",
    call2_evaluation: "",
    normal_dataya_gecebilir: "",
    reason_text: "",
  },
};

export const computeWorkDurationFromHireDate = (iseGirisTarihi) => {
  if (!iseGirisTarihi) return "";
  const start = new Date(String(iseGirisTarihi).slice(0, 10));
  if (Number.isNaN(start.getTime())) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today - start) / 86400000);
  if (diffDays < 0) return "";
  if (diffDays < 30) return `${diffDays} gün`;
  const months = Math.floor(diffDays / 30);
  return `${months} ay (${diffDays} gün)`;
};

export const agentMetaPatchFromAgent = (agent) => {
  if (!agent) {
    return {
      agent_id: "",
      assistant_name: "",
      work_duration: "",
      yeni_dataya_gecis_tarihi: "",
    };
  }
  const hire = agent.ise_giris_tarihi || agent.hire_date || "";
  const yeniData = agent.yeni_dataya_gecis_tarihi || "";
  const workDuration = agent.work_duration
    || computeWorkDurationFromHireDate(hire);
  return {
    agent_id: agent.id != null ? String(agent.id) : "",
    assistant_name: agent.ad_soyad || agent.name || "",
    work_duration: workDuration,
    yeni_dataya_gecis_tarihi: yeniData ? String(yeniData).slice(0, 10) : "",
  };
};

export const agentsForSearchSelect = (agents = []) => (agents || []).map((a) => ({
  id: a.id,
  ad_soyad: a.ad_soyad || a.name || "",
  kullanici_adi: a.kullanici_adi || "",
  gsm: a.gsm || "",
  ise_giris_tarihi: a.ise_giris_tarihi || "",
  yeni_dataya_gecis_tarihi: a.yeni_dataya_gecis_tarihi || "",
  work_duration: a.work_duration || "",
}));

export const createDraftRow = (reportType, defaults = {}) => ({
  _draftId: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  ...(EMPTY_ROW[reportType] || {}),
  ...defaults,
});

/** Tk. Lideri ses kaydı → Kalite Değerlendirme taslak aktarımı (sessionStorage) */
export const MGR_KALITE_PREFILL_FROM_TL_KEY = "mgrKalitePrefillFromTl_v1";

export const buildKaliteDraftFromTlRecording = (tlRow) => {
  if (!tlRow) return null;
  const reportDate = tlRow.report_date ? String(tlRow.report_date).slice(0, 10) : "";
  return createDraftRow("kalite-degerlendirme", {
    leader_user_id: tlRow.leader_user_id != null ? String(tlRow.leader_user_id) : "",
    leader_name: tlRow.leader_name || "",
    agent_id: tlRow.agent_id != null ? String(tlRow.agent_id) : "",
    assistant_name: tlRow.assistant_name || "",
    week_number: tlRow.week_number ?? "",
    report_date: reportDate,
    call1_recording_url: tlRow.call1_recording_url || "",
    call2_recording_url: tlRow.call2_recording_url || "",
    _source_tl_recording_id: tlRow.id ?? null,
  });
};

export const storeKalitePrefillFromTl = (tlRow) => {
  const draft = buildKaliteDraftFromTlRecording(tlRow);
  if (!draft) return false;
  sessionStorage.setItem(
    MGR_KALITE_PREFILL_FROM_TL_KEY,
    JSON.stringify({ draft, tlRecordingId: tlRow.id, at: Date.now() }),
  );
  return true;
};

export const consumeKalitePrefillFromTl = () => {
  try {
    const raw = sessionStorage.getItem(MGR_KALITE_PREFILL_FROM_TL_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(MGR_KALITE_PREFILL_FROM_TL_KEY);
    const parsed = JSON.parse(raw);
    return parsed?.draft || null;
  } catch {
    return null;
  }
};

const normalizeYesNo = (value) => {
  const v = String(value || "").toLowerCase().trim();
  if (v === "evet" || v === "hayir" || v === "hayır") return v === "hayır" ? "hayir" : v;
  return "";
};

export const formRowToPayload = (reportType, row) => {
  const keys = fieldKeysForType(reportType);
  const payload = { report_type: reportType, assistant_name: String(row?.assistant_name || "").trim() || null };
  keys.forEach((key) => {
    if (key === "assistant_name") return;
    if (key === "agent_id" || key === "leader_user_id" || key === "quality_user_id") {
      payload[key] = row?.[key] ? Number(row[key]) : null;
      return;
    }
    if (key === "week_number") {
      payload[key] = row?.[key] === "" || row?.[key] == null ? null : Number(row[key]);
      return;
    }
    if (key === "report_date") {
      payload[key] = row?.[key] ? String(row[key]).slice(0, 10) : null;
      return;
    }
    if (key === "assistant_name" || key === "leader_name" || key === "quality_user_name") {
      payload[key] = String(row?.[key] || "").trim() || null;
      return;
    }
    if (key === "yeni_dataya_gecis_tarihi") {
      payload[key] = row?.[key] ? String(row[key]).slice(0, 10) : null;
      return;
    }
    const col = getReportColumns(reportType).find((c) => c.key === key);
    if (col?.type === "yesno") {
      const yn = normalizeYesNo(row?.[key]);
      payload[key] = yn || null;
      return;
    }
    const raw = row?.[key];
    payload[key] = raw === null || raw === undefined || raw === "" ? null : raw;
  });
  return payload;
};

export const mapItemToForm = (reportType, item) => {
  const base = { ...(EMPTY_ROW[reportType] || {}) };
  if (!item) return base;
  fieldKeysForType(reportType).forEach((key) => {
    if (item[key] !== undefined && item[key] !== null) {
      if (key === "agent_id" || key === "leader_user_id" || key === "quality_user_id") {
        base[key] = String(item[key]);
      } else if (key === "yeni_dataya_gecis_tarihi" || key === "report_date") {
        base[key] = String(item[key]).slice(0, 10);
      } else if (key === "week_number") {
        base[key] = String(item[key]);
      } else {
        base[key] = item[key];
      }
    }
  });
  if (item.assistant_name) base.assistant_name = item.assistant_name;
  if (item.leader_name) base.leader_name = item.leader_name;
  if (item.quality_user_name) base.quality_user_name = item.quality_user_name;
  return base;
};

export const buildExcelRows = (reportType, items) => {
  const columns = getReportColumns(reportType);
  const headerRow = columns.map((col) => col.label);
  const dataRows = (items || []).map((row) => columns.map((col) => {
    const value = row?.[col.key];
    if (col.type === "yesno") return formatOnayLabel(value);
    if (col.type === "leader_select") return row?.leader_name || value || "";
    if (col.type === "quality_select") return row?.quality_user_name || value || "";
    if (col.type === "agent_select") return row?.assistant_name || value || "";
    if (col.type === "week_display") {
      if (!row?.week_number && !row?.report_date) return "";
      const isoYear = resolveIsoYearForRow(row, getCurrentIsoWeekContext().isoYear);
      return formatWeekRangeLabel(isoYear, row?.week_number);
    }
    if (col.type === "date" || col.key === "yeni_dataya_gecis_tarihi") {
      return formatReportDate(value);
    }
    if (value === null || value === undefined) return "";
    return value;
  }));
  return [headerRow, ...dataRows];
};

export const truncateReportText = (value, max = 80) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
};
